import { Fragment, useMemo, useState } from 'react'

export interface HedgeOperation {
  id: number
  direction: string
  status: string
  prop_open_price: number | null
  broker_open_price: number | null
  prop_close_price: number | null
  broker_close_price: number | null
  prop_lots: number | null
  broker_lots: number | null
  sl_usd?: number | null
  tp_usd?: number | null
  prop_pnl: number | null
  broker_pnl: number | null
  total_pnl: number | null
  signal_source: string
  created_at: string | null
  closed_at: string | null
  prop_ticket: number | null
  broker_ticket: number | null
}

interface Props {
  operations: HedgeOperation[]
  equity: number | null
  balance: number | null
  cyclePnl: number | null
  initialPageSize?: number
}

const DEFAULT_PAGE = 10

function fmtMoney(v: number | null | undefined, decimals = 2): string {
  if (v === null || v === undefined) return '—'
  const sign = v >= 0 ? '' : '-'
  return `${sign}$${Math.abs(v).toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

function fmtPrice(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  return v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 5 })
}

function fmtDate(s: string | null): string {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return s }
}

function pnlColor(v: number | null | undefined): string {
  if (v === null || v === undefined || v === 0) return 'text-[#94a3b8]'
  return v > 0 ? 'text-emerald-400' : 'text-rose-400'
}

function pnlSign(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v > 0 ? '+' : ''
}

/**
 * Copertura per singola coppia:
 *   broker_pnl / abs(prop_pnl) × 100
 *   - prop in loss + broker in profit → positiva (verde): % di perdita coperta
 *   - prop in profit + broker in loss → negativa (rossa): % di profitto eroso dalla perdita broker
 *   - stesso segno → ratio (informativo)
 */
function coveragePct(propPnl: number | null, brokerPnl: number | null): number | null {
  if (propPnl === null || propPnl === undefined || brokerPnl === null || brokerPnl === undefined) return null
  if (propPnl === 0) return null
  return (brokerPnl / Math.abs(propPnl)) * 100
}

// Colore copertura: stesso schema del P&L (positivo = verde, negativo = rosso, null/0 = grigio)
function coverageColor(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || pct === 0) return 'text-[#94a3b8]'
  return pct > 0 ? 'text-emerald-400' : 'text-rose-400'
}

export function HedgePairView({ operations, equity, balance, cyclePnl, initialPageSize = DEFAULT_PAGE }: Props) {
  const [showAll, setShowAll] = useState(false)
  const [showTech, setShowTech] = useState(false)

  // Totali storici derivati dalle operazioni
  const totals = useMemo(() => {
    let propTot = 0, brokerTot = 0, totTot = 0
    let coverNum = 0, coverDen = 0
    let hasProp = false, hasBroker = false
    for (const op of operations) {
      if (op.prop_pnl !== null && op.prop_pnl !== undefined) { propTot += op.prop_pnl; hasProp = true }
      if (op.broker_pnl !== null && op.broker_pnl !== undefined) { brokerTot += op.broker_pnl; hasBroker = true }
      if (op.total_pnl !== null && op.total_pnl !== undefined) { totTot += op.total_pnl }
      // copertura media pesata: somma broker_pnl (può essere negativo) quando prop in loss / somma prop_loss
      if (op.prop_pnl !== null && op.prop_pnl !== undefined && op.prop_pnl < 0) {
        coverDen += Math.abs(op.prop_pnl)
        if (op.broker_pnl !== null && op.broker_pnl !== undefined) {
          coverNum += op.broker_pnl
        }
      }
    }
    return {
      propTot: hasProp ? propTot : null,
      brokerTot: hasBroker ? brokerTot : null,
      totTot: hasProp || hasBroker ? totTot : null,
      coverAvg: coverDen > 0 ? (coverNum / coverDen) * 100 : null,
    }
  }, [operations])

  // Snapshot live: rapporto broker_tot / abs(prop_pnl_live). Mostrato sempre se i dati ci sono.
  // Verde se broker positivo (copre perdita o aggiunge profitto), rosso se broker negativo (erode).
  const liveCoverage = useMemo(() => {
    if (cyclePnl === null || cyclePnl === undefined || cyclePnl === 0) return null
    if (totals.brokerTot === null) return null
    return (totals.brokerTot / Math.abs(cyclePnl)) * 100
  }, [cyclePnl, totals.brokerTot])

  const visible = showAll ? operations : operations.slice(0, initialPageSize)
  const hidden = Math.max(0, operations.length - visible.length)

  return (
    <div className="space-y-3">
      {/* SNAPSHOT LIVE */}
      <div className="rounded-xl border border-[#1e3a5f]/60 bg-[#0d1529] p-3">
        <div className="text-[10px] text-[#64748b] uppercase tracking-wider font-bold mb-2">▣ Snapshot live</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border-r border-[#1e293b] pr-3">
            <div className="text-[9px] text-[#64748b] uppercase font-bold">PROP equity</div>
            <div className="text-[16px] font-bold text-white tabular-nums">{fmtMoney(equity)}</div>
            <div className={`text-[11px] font-semibold tabular-nums ${pnlColor(cyclePnl)}`}>
              Δ: {pnlSign(cyclePnl)}{fmtMoney(cyclePnl)}
            </div>
          </div>
          <div className="border-r border-[#1e293b] pr-3">
            <div className="text-[9px] text-[#64748b] uppercase font-bold">BROKER P&L cumulato</div>
            <div className={`text-[16px] font-bold tabular-nums ${pnlColor(totals.brokerTot)}`}>
              {pnlSign(totals.brokerTot)}{fmtMoney(totals.brokerTot)}
            </div>
            <div className="text-[10px] text-[#64748b]">somma broker P&L su {operations.length} op</div>
          </div>
          <div>
            <div className="text-[9px] text-[#64748b] uppercase font-bold">Copertura live</div>
            <div className={`text-[16px] font-bold tabular-nums ${coverageColor(liveCoverage)}`}>
              {liveCoverage === null ? '—' : `${pnlSign(liveCoverage)}${liveCoverage.toFixed(1)}%`}
            </div>
            <div className="text-[10px] text-[#64748b]">
              {liveCoverage === null ? '—' : `(${pnlSign(totals.brokerTot)}${fmtMoney(totals.brokerTot)} / ${fmtMoney(Math.abs(cyclePnl ?? 0))})`}
            </div>
          </div>
        </div>
      </div>

      {/* OPERAZIONI ACCOPPIATE */}
      <div className="rounded-xl border border-[#1e3a5f]/60 overflow-hidden">
        <div className="bg-[#0d1529] px-3 py-2 flex items-center justify-between gap-3">
          <span className="text-[10px] text-[#64748b] uppercase tracking-wider font-bold">
            ▣ Operazioni accoppiate ({operations.length})
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTech(s => !s)}
              className={`text-[10px] transition-colors font-bold ${
                showTech ? 'text-amber-300 hover:text-amber-200' : 'text-[#64748b] hover:text-white'
              }`}
              title="Mostra/nascondi prezzi precisi, SL e TP"
            >
              {showTech ? '▼ Nascondi dettagli tecnici' : '▶ Dettagli tecnici'}
            </button>
            {operations.length > initialPageSize && (
              <button
                onClick={() => setShowAll(s => !s)}
                className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors font-bold"
              >
                {showAll ? `Mostra solo prime ${initialPageSize}` : `Mostra tutte (${hidden} altre)`}
              </button>
            )}
          </div>
        </div>

        {operations.length === 0 ? (
          <div className="p-8 text-center text-[#64748b] text-[12px]">Nessuna operazione</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-[#0a1020] text-[#64748b] uppercase text-[9px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-[45%]" colSpan={2}>PROP</th>
                  <th className="text-left px-3 py-2 w-[45%] border-l border-[#1e293b]" colSpan={2}>BROKER</th>
                  <th className="text-center px-3 py-2 border-l border-[#1e293b]">Cop. %</th>
                </tr>
                <tr className="text-[8px]">
                  <th className="text-left px-3 pb-1.5 font-normal">Ticket / Dir / Lotti</th>
                  <th className="text-right px-3 pb-1.5 font-normal">Open → Close · P&L</th>
                  <th className="text-left px-3 pb-1.5 font-normal border-l border-[#1e293b]">Ticket / Dir / Lotti</th>
                  <th className="text-right px-3 pb-1.5 font-normal">Open → Close · P&L</th>
                  <th className="border-l border-[#1e293b]"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map(op => {
                  const cov = coveragePct(op.prop_pnl, op.broker_pnl)
                  const propDirClass = op.direction === 'BUY' ? 'text-sky-400' : 'text-rose-400'
                  const brokerDir = op.direction === 'BUY' ? 'SELL' : 'BUY'  // hedge opposto
                  const brokerDirClass = brokerDir === 'BUY' ? 'text-sky-400' : 'text-rose-400'
                  const isOpen = op.status !== 'CLOSED'
                  return (
                  <Fragment key={op.id}>
                    <tr className="border-t border-[#1e293b]/60 align-top hover:bg-[#0f1d35]/40">
                      {/* PROP col 1: ticket / dir / lotti */}
                      <td className="px-3 py-2">
                        <div className="text-[#94a3b8]">
                          #{op.prop_ticket ?? op.id}
                          {' '}
                          <span className={`font-bold ${propDirClass}`}>{op.direction}</span>
                          {' '}
                          <span className="text-white">{op.prop_lots ?? '—'}</span>
                          {' lots'}
                        </div>
                        <div className="text-[9px] text-[#64748b] mt-0.5">{fmtDate(op.created_at)}</div>
                      </td>
                      {/* PROP col 2: prezzi + pnl */}
                      <td className="px-3 py-2 text-right">
                        <div className="text-[#94a3b8] tabular-nums">
                          {fmtPrice(op.prop_open_price)}
                          {' → '}
                          <span className={isOpen ? 'text-amber-300' : ''}>
                            {isOpen ? 'aperta' : fmtPrice(op.prop_close_price)}
                          </span>
                        </div>
                        <div className={`font-bold tabular-nums mt-0.5 ${pnlColor(op.prop_pnl)}`}>
                          {pnlSign(op.prop_pnl)}{fmtMoney(op.prop_pnl)}
                        </div>
                      </td>
                      {/* BROKER col 1 */}
                      <td className="px-3 py-2 border-l border-[#1e293b]/60">
                        <div className="text-[#94a3b8]">
                          #{op.broker_ticket ?? '—'}
                          {' '}
                          <span className={`font-bold ${brokerDirClass}`}>{brokerDir}</span>
                          {' '}
                          <span className="text-white">{op.broker_lots ?? '—'}</span>
                          {' lots'}
                        </div>
                        <div className="text-[9px] text-[#64748b] mt-0.5">
                          {op.closed_at ? `chiusa ${fmtDate(op.closed_at)}` : ''}
                        </div>
                      </td>
                      {/* BROKER col 2 */}
                      <td className="px-3 py-2 text-right">
                        <div className="text-[#94a3b8] tabular-nums">
                          {fmtPrice(op.broker_open_price)}
                          {' → '}
                          <span className={isOpen ? 'text-amber-300' : ''}>
                            {isOpen ? 'aperta' : fmtPrice(op.broker_close_price)}
                          </span>
                        </div>
                        <div className={`font-bold tabular-nums mt-0.5 ${pnlColor(op.broker_pnl)}`}>
                          {pnlSign(op.broker_pnl)}{fmtMoney(op.broker_pnl)}
                        </div>
                      </td>
                      {/* COPERTURA (può essere negativa se broker in loss) */}
                      <td className={`px-3 py-2 text-center font-bold tabular-nums border-l border-[#1e293b]/60 ${coverageColor(cov)}`}>
                        {cov === null ? '—' : `${pnlSign(cov)}${cov.toFixed(0)}%`}
                      </td>
                    </tr>

                    {/* Sub-row dettagli tecnici (a scomparsa) */}
                    {showTech && (
                      <tr className="bg-[#0a1020]/60">
                        <td colSpan={5} className="px-3 py-2 border-t border-dashed border-[#1e293b]/60">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[10px]">
                            {/* PROP */}
                            <div>
                              <span className="text-[#64748b] uppercase font-bold tracking-wider">PROP</span>
                              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[#94a3b8] tabular-nums">
                                <span><span className="text-[#64748b]">apertura:</span> {fmtPrice(op.prop_open_price)}</span>
                                <span><span className="text-[#64748b]">chiusura:</span> {op.prop_close_price !== null && op.prop_close_price !== undefined ? fmtPrice(op.prop_close_price) : <span className="text-amber-300">— (aperta)</span>}</span>
                              </div>
                            </div>
                            {/* BROKER */}
                            <div>
                              <span className="text-[#64748b] uppercase font-bold tracking-wider">BROKER</span>
                              <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[#94a3b8] tabular-nums">
                                <span><span className="text-[#64748b]">apertura:</span> {fmtPrice(op.broker_open_price)}</span>
                                <span><span className="text-[#64748b]">chiusura:</span> {op.broker_close_price !== null && op.broker_close_price !== undefined ? fmtPrice(op.broker_close_price) : <span className="text-amber-300">— (aperta)</span>}</span>
                              </div>
                            </div>
                            {/* SL/TP comuni (espressi in $ sulla PROP) */}
                            <div className="md:col-span-2 pt-1 border-t border-[#1e293b]/40">
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[#94a3b8] tabular-nums">
                                <span>
                                  <span className="text-[#64748b]">SL (stop loss):</span>{' '}
                                  {op.sl_usd !== undefined && op.sl_usd !== null
                                    ? <span className="text-rose-400/90 font-semibold">{fmtMoney(-Math.abs(op.sl_usd))}</span>
                                    : <span className="text-[#475569]">—</span>}
                                </span>
                                <span>
                                  <span className="text-[#64748b]">TP (take profit):</span>{' '}
                                  {op.tp_usd !== undefined && op.tp_usd !== null
                                    ? <span className="text-emerald-400/90 font-semibold">{fmtMoney(Math.abs(op.tp_usd))}</span>
                                    : <span className="text-[#475569]">—</span>}
                                </span>
                                <span className="text-[#64748b]">
                                  sorgente segnale: <span className="text-[#94a3b8]">{op.signal_source ? op.signal_source.replace(/jifu/gi, 'sempai') : '—'}</span>
                                </span>
                                <span className="text-[#64748b]">
                                  stato: <span className="text-[#94a3b8]">{op.status}</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* TOTALI STORICI */}
      {operations.length > 0 && (
        <div className="rounded-xl border border-[#1e3a5f]/60 bg-[#0d1529] p-3">
          <div className="text-[10px] text-[#64748b] uppercase tracking-wider font-bold mb-2">
            ▣ Totali storici ciclo (somma di {operations.length} operazioni)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border-r border-[#1e293b] pr-3">
              <div className="text-[9px] text-[#64748b] uppercase font-bold">PROP totale</div>
              <div className={`text-[16px] font-bold tabular-nums ${pnlColor(totals.propTot)}`}>
                {pnlSign(totals.propTot)}{fmtMoney(totals.propTot)}
              </div>
            </div>
            <div className="border-r border-[#1e293b] pr-3">
              <div className="text-[9px] text-[#64748b] uppercase font-bold">BROKER totale</div>
              <div className={`text-[16px] font-bold tabular-nums ${pnlColor(totals.brokerTot)}`}>
                {pnlSign(totals.brokerTot)}{fmtMoney(totals.brokerTot)}
              </div>
            </div>
            <div className="border-r border-[#1e293b] pr-3">
              <div className="text-[9px] text-[#64748b] uppercase font-bold">Copertura media</div>
              <div className={`text-[16px] font-bold tabular-nums ${coverageColor(totals.coverAvg)}`}>
                {totals.coverAvg === null ? '—' : `${totals.coverAvg.toFixed(1)}%`}
              </div>
              <div className="text-[10px] text-[#64748b]">pesata su loss PROP</div>
            </div>
            <div>
              <div className="text-[9px] text-[#64748b] uppercase font-bold">Netto totale</div>
              <div className={`text-[16px] font-bold tabular-nums ${pnlColor(totals.totTot)}`}>
                {pnlSign(totals.totTot)}{fmtMoney(totals.totTot)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HedgePairView
